import { useEffect } from "react";
import { Link } from "react-router-dom";
import { useFavorites } from "@/hooks/useFavorites";
import { MAX_FAVORITES } from "@/lib/favorites";

interface FavoritesDrawerProps {
  open: boolean;
  onClose: () => void;
}

export default function FavoritesDrawer({ open, onClose }: FavoritesDrawerProps) {
  const { items, remove, clear } = useFavorites();

  useEffect(() => {
    if (!open) return;
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50" role="dialog" aria-modal="true" aria-label="我的收藏">
      <button
        type="button"
        aria-label="关闭收藏面板"
        onClick={onClose}
        className="absolute inset-0 w-full h-full bg-primary-950/50 cursor-pointer"
      />
      <aside className="absolute right-0 top-0 h-full w-full sm:w-[420px] bg-background-50 flex flex-col border-l border-background-200">
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-background-200">
          <div>
            <h2 className="text-base font-semibold text-foreground-950">我的收藏</h2>
            <p className="mt-1 text-xs text-foreground-500 font-label">
              已收藏 {items.length} / {MAX_FAVORITES} 条
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="关闭"
            className="w-8 h-8 flex items-center justify-center rounded-full text-foreground-600 hover:bg-background-100 cursor-pointer -mt-1"
          >
            <i className="ri-close-line text-xl leading-none" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-4">
          {items.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center px-6 py-16">
              <span className="w-14 h-14 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
                <i className="ri-bookmark-line text-2xl leading-none" />
              </span>
              <p className="mt-4 text-sm font-medium text-foreground-900">还没有收藏任何内容</p>
              <p className="mt-2 text-xs text-foreground-500 leading-relaxed">
                在作品或资讯卡片上点击书签图标，就能把它放进这里，随时回来看。
              </p>
              <Link
                to="/works"
                onClick={onClose}
                className="mt-5 h-9 px-4 flex items-center rounded-md bg-primary-500 text-background-50 text-xs font-medium hover:bg-primary-600 transition-colors"
              >
                去逛逛作品
              </Link>
            </div>
          ) : (
            <ul className="flex flex-col gap-3">
              {items.map((entry) => (
                <li key={entry.key} className="flex gap-3 p-2.5 rounded-lg border border-background-200 bg-background-50">
                  <Link
                    to={`/${entry.type === "work" ? "works" : "news"}/${entry.id}`}
                    onClick={onClose}
                    className="shrink-0 w-14 h-[74px] rounded-md overflow-hidden bg-background-200"
                  >
                    <img
                      src={entry.cover}
                      alt={entry.title}
                      title={entry.title}
                      className="w-full h-full object-cover object-top"
                    />
                  </Link>
                  <div className="flex-1 min-w-0 flex flex-col">
                    <Link
                      to={`/${entry.type === "work" ? "works" : "news"}/${entry.id}`}
                      onClick={onClose}
                      className="text-sm font-medium text-foreground-950 leading-snug line-clamp-2 hover:text-primary-600"
                    >
                      {entry.title}
                    </Link>
                    <p className="mt-1 text-[11px] text-foreground-500 font-label truncate">{entry.meta}</p>
                    <div className="mt-auto pt-2 flex items-center justify-between">
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-secondary-100 text-secondary-800">
                        {entry.type === "work" ? "作品" : "资讯"}
                      </span>
                      <button
                        type="button"
                        onClick={() => remove(entry.key)}
                        className="text-[11px] text-foreground-500 hover:text-accent-600 cursor-pointer whitespace-nowrap"
                      >
                        取消收藏
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="px-4 py-3 border-t border-background-200 bg-background-100/70">
          <div className="flex items-center justify-between gap-3">
            <p className="text-[11px] text-foreground-500 leading-relaxed">
              收藏保存在本机浏览器，最多 {MAX_FAVORITES} 条
            </p>
            {items.length > 0 && (
              <button
                type="button"
                onClick={clear}
                className="text-[11px] text-accent-700 hover:text-accent-800 cursor-pointer whitespace-nowrap"
              >
                清空
              </button>
            )}
          </div>
        </div>
      </aside>
    </div>
  );
}